// Stencil
import {
  Component,
  Element,
  Fragment,
  getAssetPath,
  h,
  Host,
  Method,
  Prop,
  State
} from "@stencil/core";

// Other Libraries
import { getIconPath, MercuryBundles } from "@genexus/mercury";

// Custom Imports
import {
  ActionListItemActionable,
  ActionListItemModel,
  ActionListItemModelExtended,
  ChActionListRenderCustomEvent,
  ChEditCustomEvent
} from "@genexus/chameleon-controls-library";
import { RadioGroupItemModel } from "@genexus/chameleon-controls-library/dist/types/components/radio-group/types";
import { config } from "../../../common/config";
import {
  ControlValidation,
  validateControls
} from "../../../common/form-validation";
import { Locale } from "../../../common/locale";
import { FormSubmitResult } from "../../../common/types";
import { GXServerConnectionData } from "../connect-gx-server/connect-gx-server";
import { KBVersion } from "../version-control/common/types";
import { mapKbsModelToActionListModel } from "./helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "components/list-box",
  "components/tooltip",
  "chameleon/scrollbar"
];

const INFO_ICON = getIconPath({
  category: "system",
  name: "information",
  colorType: "on-surface"
});

const FOLDER_ICON = getIconPath({
  category: "system",
  name: "folder",
  colorType: "primary"
});

const LOCATION_MODE_DEFAULT = "default";
const LOCATION_MODE_CUSTOM = "custom";

@Component({
  tag: "gx-ide-create-kb-from-server",
  styleUrl: "create-kb-from-server.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/create-kb-from-server"]
})
export class GxIdeCreateKbFromServer {
  #chSearchIcon = getIconPath({
    category: "system",
    name: "search",
    colorType: "interactive"
  });

  #showMoreIcon = getIconPath({
    category: "system",
    name: "more-horizontal",
    colorType: "interactive"
  });

  #versionsSelect: RadioGroupItemModel[] = [
    { value: "Trunk", caption: "Trunk Version" },
    { value: "All", caption: "All Versions" },
    { value: "Select", caption: "Select Version" }
  ];

  [key: string]: any;

  #_componentLocale: any;
  #kbsActionListModel: ActionListItemModel[] = [];
  #kbVersions: KBVersion[] = [];
  #chShortcutsEl: HTMLChShortcutsElement;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/create-kb-from-server/shortcuts.json`
  );
  #selectedKbId: string;
  #kbNameAlreadyEditedByUser: boolean = false;

  #locationModeModel: RadioGroupItemModel[];

  #versionSelectedTypeEl: HTMLChRadioGroupRenderElement;

  @Element() el: HTMLGxIdeCreateKbFromServerElement;

  #localKBNameEl!: HTMLChEditElement;

  /**
   * State to enable/disable the select versions button
   */
  @State() enableSelectVersionsButton: boolean = false;

  /**
   * The current kbn name value.
   */
  @State() localKbNameInputValue: string = "";

  /**
   * State to store the loading state of the component
   */
  @State() loading = true;

  /**
   * State to store the filter input value to filter the list-box
   */
  @State() filterValue: string = "";

  /**
   * KBs filtered
   */
  @State() kbsFilteredActionListModel: ActionListItemModel[] = [];

  @State() validatableControls = new Map<string, ControlValidation>();

  /**
   * The version selected type
   */
  @State() versionSelectedType: VersionOption;

  /**
   * String value to show the number of versions selected ("N versions selected")
   */
  @State() versionsValueInput: string = "";

  @State() locationMode: string = LOCATION_MODE_DEFAULT;

  /**
   * Default suggested path to a directory where the KB will be stored/generated.
   */
  @Prop({ mutable: true }) kbLocation: string;

  /**
   * Specifies whether the location field (and its folder-picker button) is
   * shown in the header, allowing the user to override the default `kbLocation`
   * path. Independent from `isAdvanced`.
   */
  @Prop() readonly canSetLocation: boolean = false;

  /**
   * Set of data required when executing any of the callbacks that require a connection to the server.
   */
  @Prop() readonly connectionData: GXServerConnectionData;

  /**
   * Callback that is invoked when the dialog is closed
   */
  @Prop() readonly cancelCallback!: () => Promise<void>;

  /**
   * The host reopens the gx-ide-connect-server login modal with the previous data, and closes the current modal
   */
  @Prop() readonly changeConnectionCallback: (
    data: GXServerConnectionData
  ) => Promise<void>;

  /**
   * Callback that must be invoked when the 'Create KB' button is pressed. Receives the user's parameter selection to create the remote KB locally and ends the dialog.
   */
  @Prop() readonly createKBCallback!: (
    kbInfo: KnowledgeBaseInfo
  ) => Promise<FormSubmitResult>;

  /**
   * Callback that must be invoked when invoking the dialog
   */
  @Prop() readonly getGXserverKBsCallback!: (
    data: GXServerConnectionData
  ) => Promise<KBData[]>;

  /**
   * Callback that returns a folder path selected by the user.
   */
  @Prop() readonly selectLocationCallback: SelectLocationCallback;

  /**
   *  Callback invoked by pressing the ellipsis in the version selector. Receives the KB selected by the user, and displays the version selector dialog. Returns a list of versions chosen by the user.
   */
  @Prop() readonly selectKBVersionsCallback: (
    selectedKB: string
  ) => Promise<KBVersion[]>;

  async componentDidLoad() {
    await this.#init();
    this.loading = false;
  }

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);

    this.#locationModeModel = [
      {
        value: LOCATION_MODE_DEFAULT,
        caption: this.#_componentLocale.kb.kbLocationDefault
      },
      {
        value: LOCATION_MODE_CUSTOM,
        caption: this.#_componentLocale.kb.kbLocationCustom
      }
    ];
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  #cancelHandler = (): void => {
    this.cancelCallback();
  };

  #changeConnectionHandler = async (): Promise<void> => {
    this.changeConnectionCallback({
      authenticationType: this.connectionData.authenticationType,
      serverUrl: this.connectionData.serverUrl,
      userName: this.connectionData.userName,
      userPassword: this.connectionData.userPassword
    });
  };

  #locationModeChangeHandler = (event: CustomEvent<string>) => {
    this.locationMode = event.detail as string;
    if (this.locationMode === LOCATION_MODE_DEFAULT) {
      this.kbLocation = "";
    }
  };

  #selectLocationHandler = async (): Promise<void> => {
    const location = await this.selectLocationCallback();
    if (location) {
      this.kbLocation = location;
    }
  };

  #inputKbLocationHandler = (event: ChEditCustomEvent<string> | InputEvent) => {
    this.kbLocation = event.detail as string;
  };

  #createHandler = async (): Promise<void> => {
    if (this.versionSelectedType !== "Select") {
      this.#kbVersions = [];
    }
    this.createKBCallback({
      selectedKB: this.#selectedKbId,
      versionSelected: this.versionSelectedType,
      localKBName: this.localKbNameInputValue,
      kbLocation:
        this.locationMode === LOCATION_MODE_CUSTOM
          ? this.kbLocation || undefined
          : undefined,
      versionList: this.#kbVersions
    }).then((formSubmitResult: FormSubmitResult) => {
      this.validatableControls = validateControls(
        formSubmitResult,
        this.validatableControls
      );
    });
  };

  // TODO: evaluate if this should be a global helper function.
  // It is used on other dialogs as well.
  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  #filterValueChangedHandler = (
    event: ChEditCustomEvent<string> | InputEvent
  ) => {
    this.filterValue = (event.detail as string).toLowerCase();
    this.kbsFilteredActionListModel = this.#kbsActionListModel.filter(
      (item: ActionListItemActionable) =>
        item.caption.toLowerCase().includes(this.filterValue)
    );
  };

  #init = async () => {
    await this.getGXserverKBsCallback({
      authenticationType: this.connectionData.authenticationType,
      serverUrl: this.connectionData.serverUrl,
      userName: this.connectionData.userName,
      userPassword: this.connectionData.userPassword
    }).then(result => {
      this.#kbsActionListModel = mapKbsModelToActionListModel(result);
      this.kbsFilteredActionListModel = this.#kbsActionListModel;
      this.filterValue = "";
    });
  };

  #initializeValidatableControls = () => {
    // populate validatableControls with the controls that could have errors.
    const validatableControls: HTMLElement[] = [this.#localKBNameEl];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  #inputLocalKbNameHandler = (
    event: ChEditCustomEvent<string> | InputEvent
  ) => {
    this.localKbNameInputValue = event.detail as string;
    this.#kbNameAlreadyEditedByUser = true;
  };

  #selectedVersionHandler = async () => {
    if (this.selectKBVersionsCallback) {
      await this.selectKBVersionsCallback(this.#selectedKbId).then(result => {
        if (result?.length) {
          this.#kbVersions = result.map(kbVersion => ({
            id: kbVersion.id,
            name: kbVersion.name
          }));

          if (result.length === 1 && this.#kbVersions[0].id === 0) {
            this.localKbNameInputValue = this.#selectedKbId;
            this.versionsValueInput =
              this.#_componentLocale.kb.versionsPlaceholder;
          } else if (result.length > 1) {
            this.localKbNameInputValue = this.#selectedKbId;
            this.versionsValueInput = `${result.length.toString()} ${
              this.#_componentLocale.kb.versionSelectedFieldValue
            }`;
          } else {
            this.localKbNameInputValue = `${this.#selectedKbId}_${
              this.#kbVersions[0].name
            }`;
            this.versionsValueInput = `${result[0].name} ${
              this.#_componentLocale.kb.oneVersionSelectedFieldValue
            }`;
          }
        }
      });
    }
  };

  #selectKbChangeHandler = (
    event: ChActionListRenderCustomEvent<ActionListItemModelExtended[]>
  ): void => {
    const item = event.detail[0]?.item as ActionListItemActionable;
    if (item === undefined) {
      this.enableSelectVersionsButton = false;
      this.#selectedKbId = undefined;
      return;
    }
    this.enableSelectVersionsButton = true;
    this.#selectedKbId = item.id;
    if (this.#kbNameAlreadyEditedByUser) {
      return;
    }
    this.localKbNameInputValue = item.caption;
    if (this.versionSelectedType === "Select") {
      this.versionsValueInput = "";
      this.#kbVersions = [];
    }
  };

  #versionsChangedHandler = (event: CustomEvent<string>) => {
    this.versionSelectedType = event.detail as VersionOption;
  };

  #sortItems = (model: ActionListItemActionable[]): void => {
    model.sort((a, b) =>
      a.caption
        ?.toLocaleLowerCase()
        .localeCompare(b.caption?.toLocaleLowerCase())
    );
  };

  #renderFooter = () => {
    return (
      <footer class="footer dialog-footer-with-border spacing-body-inline spacing-body-block-end">
        <div class="buttons-spacer">
          <button
            // cancel button
            class="button-secondary"
            onClick={this.#cancelHandler}
            part="button button-cancel"
          >
            {this.#_componentLocale.footer.cancel}
          </button>

          <button
            // create kb button
            class="button-primary"
            onClick={this.enableSelectVersionsButton && this.#createHandler}
            disabled={!this.enableSelectVersionsButton}
            part="button button-create-kb"
          >
            {this.#_componentLocale.footer.createKb}
          </button>
        </div>
      </footer>
    );
  };

  render(): void {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>

        <section class="section">
          <header class="header dialog-header-with-border space-body spacing-body-inline spacing-body-block-start">
            <div class="field field-block">
              <label class="label">
                {this.#_componentLocale.header.serverUrl}
              </label>
              <p>{this.connectionData.serverUrl}</p>
            </div>

            <div class="field field-block">
              <label class="label">{this.#_componentLocale.header.user}</label>
              <p>{this.connectionData.userName}</p>
            </div>

            <button
              id="change-connection-button"
              class="button-tertiary button-change"
              onClick={this.#changeConnectionHandler}
            >
              {this.#_componentLocale.header.change}
            </button>
          </header>
          <section class="main field-group spacing-body">
            {this.loading ? (
              <gx-ide-loader
                loaderTitle={this.#_componentLocale.loader.title}
                description={this.#_componentLocale.loader.description}
                show
              ></gx-ide-loader>
            ) : (
              <Fragment>
                <ch-edit
                  class="input search-kbs"
                  id="local-kb-name"
                  startImgSrc={this.#chSearchIcon}
                  type="search"
                  onInput={this.#filterValueChangedHandler}
                  placeholder={this.#_componentLocale.kb.searchKbsPlaceholder}
                ></ch-edit>
                {this.#kbsActionListModel.length === 0 ||
                this.kbsFilteredActionListModel.length === 0 ? (
                  <div class="list-box">
                    <gx-ide-empty-state
                      stateIconSrc={INFO_ICON}
                      stateTitle={this.#_componentLocale.emptyKBsText}
                      isAnimated={true}
                    ></gx-ide-empty-state>
                  </div>
                ) : (
                  <ch-action-list-render
                    selection="single"
                    class="list-box"
                    onSelectedItemsChange={this.#selectKbChangeHandler}
                    model={this.kbsFilteredActionListModel}
                    sortItemsCallback={this.#sortItems}
                  ></ch-action-list-render>
                )}
                <div class="versions-container field-group">
                  <ch-radio-group-render
                    class="radio-group"
                    model={this.#versionsSelect}
                    onChange={this.#versionsChangedHandler}
                    value={this.#versionsSelect[0].value}
                    ref={(el: HTMLChRadioGroupRenderElement) => {
                      this.#versionSelectedTypeEl =
                        el as HTMLChRadioGroupRenderElement;
                      if (this.#versionSelectedTypeEl) {
                        this.versionSelectedType = this.#versionSelectedTypeEl
                          .value as VersionOption;
                      }
                    }}
                  ></ch-radio-group-render>

                  {this.versionSelectedType === "Select" && (
                    <div class="versions-container__item field-group">
                      <div class="field field-block">
                        <label class="label" htmlFor="control-id-1">
                          {this.#_componentLocale.kb.versionsLabel}
                        </label>
                        <div class="versions-container__input">
                          <ch-edit
                            class="input"
                            readonly
                            placeholder={
                              this.enableSelectVersionsButton
                                ? this.#_componentLocale.kb.versionsPlaceholder
                                : this.#_componentLocale.kb
                                    .versionsPlaceholderDisabled
                            }
                            value={this.versionsValueInput}
                          ></ch-edit>
                          <button
                            id="select-version-button"
                            class="button-tertiary button-icon-only"
                            onClick={this.#selectedVersionHandler}
                            disabled={!this.enableSelectVersionsButton}
                          >
                            <ch-image
                              src={this.#showMoreIcon}
                              class="icon-m"
                              disabled={!this.enableSelectVersionsButton}
                            ></ch-image>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div class="field field-block">
                  <label class="label" htmlFor="localKbName">
                    {this.#_componentLocale.kb.localKbNameLabel}
                  </label>
                  <ch-edit
                    // local kb name
                    class="input"
                    id="localKbName"
                    value={this.localKbNameInputValue}
                    onInput={this.#inputLocalKbNameHandler}
                    placeholder={
                      this.#_componentLocale.kb.localKbNamePlaceholder
                    }
                    ref={(el: HTMLChEditElement) => {
                      this.#localKBNameEl = el as HTMLChEditElement;
                      if (this.#localKBNameEl) {
                        this.#initializeValidatableControls();
                      }
                    }}
                  ></ch-edit>
                  {this.#evaluateTooltipRender(this.#localKBNameEl)}
                </div>

                {this.canSetLocation && (
                  <div class="field field-block">
                    <label class="label" htmlFor="kbLocation">
                      {this.#_componentLocale.kb.kbLocation}
                    </label>
                    <ch-radio-group-render
                      class="radio-group"
                      id="kbLocation"
                      model={this.#locationModeModel}
                      value={LOCATION_MODE_DEFAULT}
                      onChange={this.#locationModeChangeHandler}
                    ></ch-radio-group-render>

                    {this.locationMode === LOCATION_MODE_CUSTOM && (
                      <div class="location-custom-wrapper">
                        <div class="location-field">
                          <ch-edit
                            class="input"
                            value={this.kbLocation}
                            id="kbLocation"
                            onInput={this.#inputKbLocationHandler}
                          ></ch-edit>
                        </div>
                        <button
                          class="button-tertiary button-icon-only"
                          aria-label={
                            this.#_componentLocale.kb.selectLocationButton
                          }
                          title={this.#_componentLocale.kb.selectLocationButton}
                          onClick={this.#selectLocationHandler}
                        >
                          <ch-image
                            class="icon-m"
                            type="mask"
                            src={FOLDER_ICON}
                          ></ch-image>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </Fragment>
            )}
          </section>

          {this.#renderFooter()}
        </section>
      </Host>
    );
  }
}

export type KBData = {
  id: string;
  name: string;
};

export type ConnectionResultData = {
  formResult: FormSubmitResult;
  kbs: KBData[];
};

export type KnowledgeBaseInfo = {
  selectedKB: string;
  versionSelected: VersionOption;
  localKBName: string;
  kbLocation?: string;
  versionList: KBVersion[];
};

export type VersionOption = "Trunk" | "All" | "Select";

export type SelectLocationCallback = () => Promise<string>;
